import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultTQC5020_R01HeaderDto, TQC5020_R01HeaderDto } from './Dto_TQC5020_R01';
import { useTQC5020_R01Store } from './Store_TQC5020_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'TQC5020_R01Item-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<TQC5020_R01HeaderDto> | null>;
	onItemRowClick: (item: TQC5020_R01HeaderDto) => void;
	onClearItemSelection: () => void;
}

/** 좌측 품목 선택 그리드 */
export const Table_TQC5020_R01Item = ({
	gridRef, onItemRowClick, onClearItemSelection,
}: Props) => {
	const {
		TQC5020_R01HeaderList: itemList,
		selectedTQC5020_R01Header: selectedItem,
		TQC5020_R01HeaderColumnState: itemColumnState,
		setTQC5020_R01HeaderColumnState: setItemColumnState,
	} = useTQC5020_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
	} = useGridSettingContent({
		gridRef,
		key: 'itemCd',
		gridData: itemList,
		defaultGridData: defaultTQC5020_R01HeaderDto,
		selectedRow: selectedItem,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		modelNo: '모델명',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'itemCd')(params), []);

	const baseColumnDefs: ColDef<TQC5020_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 200,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 200,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
		},
		{
			headerName: '모델명', colId: 'modelNo', field: 'modelNo', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'modelNo'),
		},
	], [getCellStyle]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<TQC5020_R01HeaderDto>[] = useMemo(() => {
		if (itemColumnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<TQC5020_R01HeaderDto>({
			baseColumnDefs,
			columnState: itemColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [itemColumnState, settingRowIndex, baseColumnDefs, headerNames]);

	const selectedItemIdList = useMemo(
		() => (selectedItem ? [selectedItem.itemCd] : []),
		[selectedItem],
	);

	const rowClassRules = useMemo(
		() => createRowClassRules<TQC5020_R01HeaderDto>({
			idKey: 'itemCd',
			selectedList: selectedItemIdList,
			deletedList: [],
		}),
		[createRowClassRules, selectedItemIdList],
	);

	const handleRowClicked = useCallback((e: RowClickedEvent<TQC5020_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		if (e.data) onItemRowClick(e.data);
	}, [onItemRowClick]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		itemCd: `${itemList.length.toLocaleString()}건`,
	}]), [itemList]);

	return (
		<GridContextWrapper<TQC5020_R01HeaderDto & { inserted?: boolean }>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={itemList}
			initSelected={onClearItemSelection}
			defaultRowData={defaultTQC5020_R01HeaderDto}
			baseColumnDefs={itemColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setItemColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<TQC5020_R01HeaderDto>
				ref={gridRef}
				rowData={itemList}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				onRowClicked={handleRowClicked}
				onCellKeyDown={undefined}
				rowClassRules={rowClassRules}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setItemColumnState}
				restoreScroll={false}
			/>
		</GridContextWrapper>
	);
};
